import {Component, ElementRef, HostListener, OnInit, ViewChild} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {MachineModel} from "../../../model/machine-model";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {MachineModelsService} from "../../../services/machine-models.service";
import {CommonMessagesService} from "../../../services/common-messages.service";
import {ComponentCanDeactivate} from "../../../common/can-deactivate/component-can-deactivate";
import {AppConfig} from "../../../shared/global-variables";
import {MachineModelPersistRequest} from "../../../model/requests/machine-model-persist-request";
import {JsonEditorComponent} from "ang-jsoneditor";
import {TranslocoService} from "@ngneat/transloco";
import {MachineType} from "../../../model/machine-type";


@Component({
  selector: 'app-machine-model-form',
  templateUrl: './machine-model-form.component.html',
  styleUrls: ['./machine-model-form.component.scss']
})
export class MachineModelFormComponent implements OnInit, ComponentCanDeactivate {

  @ViewChild('workingModesDefinitionEditor', {static: false})
  workingModesDefinitionEditor: JsonEditorComponent;

  @ViewChild('fileUploader', {static: false})
  fileInput: ElementRef;

  public mode: 'create' | 'edit';

  public image: File = null;
  public imageBase64: any;

  public machineTypeOptions: { label: string, value: MachineType }[] = [];
  public imageErrorMessage: string;

  public machineModelForm: FormGroup;
  public persistingChanges: boolean;
  public isModelLoading: boolean = true;

  private machineModel: MachineModel;
  private formSuccess: boolean;


  constructor(private formBuilder: FormBuilder,
              private router: Router,
              private route: ActivatedRoute,
              private i18n: TranslocoService,
              private machineModelsService: MachineModelsService,
              private commonMessagesService: CommonMessagesService) {
  }

  ngOnInit(): void {
    this.route.url.subscribe(urlSegment => {
      const firstPath = urlSegment[0].path;
      if (firstPath === 'new') {
        this.mode = 'create';
        this.loadCreationForm();
      } else {
        this.mode = 'edit';
        this.loadEditionForm(firstPath);
      }
    });
  }

  private loadDropdowns(): void {
    this.i18n.selectTranslation().subscribe(() => {
      this.buildMachineTypeOptions();
    });
  }

  private buildMachineTypeOptions(): void {
    this.machineTypeOptions = [
      {label: this.i18n.translate('common.none'), value: MachineType.NONE},
      {label: this.i18n.translate('common.essential'), value: MachineType.ESSENTIAL},
      {label: this.i18n.translate('common.soul'), value: MachineType.SOUL},
      {label: this.i18n.translate('common.speed'), value: MachineType.SPEED},
      {label: this.i18n.translate('common.versatile'), value: MachineType.VERSATILE},
      {label: this.i18n.translate('common.zitrux'), value: MachineType.ZITRUX}
    ];
    this.machineTypeOptions.sort((a, b) => a.label > b.label ? 1 : -1);
  }

  loadEditionForm(uuid: string) {
    this.isModelLoading = false;
    this.machineModelsService.getModel(uuid).toPromise().then(response => {
      this.machineModel = response.machineModel;
      this.imageBase64 = this.machineModel.image;
      this.buildForm(this.machineModel);
      this.image = new File([], 'Imagen');
    }).finally(() => this.isModelLoading = false)
  }

  loadCreationForm() {
    this.machineModel = new MachineModel();
    this.buildForm(this.machineModel);
    this.isModelLoading = false;
  }

  buildForm(machineModel: MachineModel): void {
    this.machineModelForm = this.formBuilder.group({
      machineModelUuid: [machineModel.machineModelUuid],
      name: [machineModel.modelName, [Validators.required, Validators.maxLength(255), Validators.minLength(AppConfig.FORM_IDENTIFIER_MIN_LENGTH)]],
      description: [machineModel.description, [Validators.maxLength(255)]],
      machineType: [machineModel.machineType, [Validators.required]]
    });
    this.machineModelForm.markAsPending();
    this.loadDropdowns();
  }

  public dispatchClick() {
    this.fileInput.nativeElement.click();
  }

  public removeImage() {
    this.image = null;
    this.fileInput.nativeElement.value = null;
  }

  public fileChangeEvent(filesInput: any) {
    if (filesInput.target.files) {
      this.image = filesInput.target.files[0];
      this.imageErrorMessage = undefined;
      const reader = new FileReader();
      reader.readAsDataURL(this.image);
      reader.onload = () => {
        this.imageBase64 = reader.result;
        if ((3 * this.imageBase64.length / 4) / 1024 > 100) {
          this.imageErrorMessage = 'invalid.imageTooLarge';
        }
      }
    }
  }

  public isValidImage(): boolean {
    return !(this.image === null || this.imageBase64 == null || ((3 * this.imageBase64.length / 4) / 1024 > 100));
  }

  public save() {
    let promise;
    if (this.mode === 'create') {
      promise = this.machineModelsService.newModel(this.buildModelRequest()).toPromise();
    } else {
      promise = this.machineModelsService.updateModel(this.buildModelRequest()).toPromise();
    }

    promise.then(() => {
      if (this.mode === 'create') {
        this.commonMessagesService.showSuccessfullyCreatedMessage();
      } else {
        this.commonMessagesService.showSuccessfullyUpdatedMessage();
      }
      this.formSuccess = true;
      this.router.navigate(['/models']).finally();
    });
  }

  private buildModelRequest(): MachineModelPersistRequest {
    const formValue = this.machineModelForm.getRawValue();
    return {
      machineModelUuid: formValue.machineModelUuid,
      modelName: formValue.name?.trim(),
      description: formValue.description?.trim(),
      image: this.imageBase64,
      machineType: formValue.machineType
    }
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.formSuccess || this.machineModelForm?.untouched;
  }

}
